import { Component, Input, OnDestroy, Output } from '@angular/core';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { CYCLE_HANDLING, Layout, RenderingContext } from './../../../api';
import { ComponentTypeRef } from './../../../ng-api';
import {
  createSingleSubject,
  isNotNil,
  LAYOUT_TYPE_ANGULAR,
  opDistinctUntilChanged,
  opShareLast,
  perfMeasure,
  rxPipe,
  thisThenThats,
  wchCycleHandling,
  wchGetLayout,
} from './../../../utils';
import { ComponentsService } from './../../services/components/components.service';
import { li } from './../../services/logger/logger.service';
import { LayoutMappingService } from './../../services/mappings/mappings.service';
import { WchService } from './../../services/wch.service';
import { opDistinctComponentTypeRef } from './../../utils/js.utils';
import { AbstractRenderingComponent } from './../rendering/abstract-rendering.component';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'ContentrefComponent';

const PAGE_NOT_FOUND_LAYOUT: Layout = {
  templateType: LAYOUT_TYPE_ANGULAR,
  template: ComponentsService.PAGE_NOT_FOUND_LAYOUT
};
const LAYOUT_NOT_FOUND_LAYOUT: Layout = {
  templateType: LAYOUT_TYPE_ANGULAR,
  template: ComponentsService.DEFAULT_LAYOUT
};

/*
 * Returns the type object by ID
 *
 * @param aLayoutMode the layout mode to check
 * @param aRenderingContext the rendering context
 * @param aCmp the components service used to resolve the mapping from selector to type
 * @param aMapping the mapping service used to resolve from rendering context to selector
 *
 * @return the type
 */
function getType(
  aId: string,
  aLayoutMode: string | undefined,
  aRenderingContext: RenderingContext,
  aCmp: ComponentsService,
  aMapping: LayoutMappingService
): Observable<ComponentTypeRef<any>> {
  // id
  const contentId = isNotNil(aRenderingContext)
    ? aRenderingContext.id
    : undefined;
  // get type from layout
  const layout = wchGetLayout(aLayoutMode, aRenderingContext);
  const typeFromLayout = aCmp.getTypeByLayout(layout, aLayoutMode);
  // get type from selector
  const selector = aMapping.getSelector(aLayoutMode, aRenderingContext);
  const typeFromSelector = aCmp.getTypeBySelector(selector, aLayoutMode);
  // default
  const defaultLayout = isNotNil(contentId)
    ? LAYOUT_NOT_FOUND_LAYOUT
    : PAGE_NOT_FOUND_LAYOUT;
  // final fallback, now use a fallback
  const typeFromDefault = aCmp.getTypeByLayout(defaultLayout, aLayoutMode);
  // combine the fallbacks
  return thisThenThats(typeFromDefault, typeFromSelector, typeFromLayout).pipe(
    tap(type => li(LOGGER, 'Resolved type', type, 'for component', aId))
  );
}

/**
 * Combines the active pieces of the state into one single object
 */
export declare type ComponentState = [
  ComponentTypeRef<any>,
  RenderingContext,
  string
];

@Component({
  selector: 'wch-contentref',
  templateUrl: './contentref.component.html',
  styleUrls: ['./contentref.component.css'],
  preserveWhitespaces: false
})
export class ContentrefComponent extends AbstractRenderingComponent
  implements OnDestroy {
  /*
   * Cycle handling strategy
   */
  @Input() cycleHandlingStrategy?: CYCLE_HANDLING | string;

  /*
   * Rendering context used to render the component reference
   */
  @Input() renderingContext: RenderingContext;

  /*
   * Layout mode used to render the component reference.
   */
  @Input() layoutMode: string;

  /**
   * Output that will be triggered when a new component was created
   */
  @Output() readonly onComponent: ReplaySubject<any> = createSingleSubject();

  /**
   * The current type to render
   */
  @Output() readonly onType: Observable<ComponentTypeRef<any>>;

  /**
   * The component state
   */
  readonly onState: Observable<ComponentState>;

  constructor(
    aCmp: ComponentsService,
    aMapping: LayoutMappingService,
    aWchService: WchService
  ) {
    // default
    super();

    // handle
    const that = this;

    // ID of the component
    const id = that._id;

    // measure startup
    const handle = perfMeasure(LOGGER);

    // attach the change handler
    const onRenderingContext: Observable<RenderingContext> = rxPipe(
      that.onRenderingContext,
      switchMap<RenderingContext, RenderingContext>(rc =>
        aWchService.resolveRenderingContext(
          rc,
          wchCycleHandling(that.cycleHandlingStrategy)
        )
      ),
      opShareLast
    );

    // handle the layout mode
    const onLayoutMode: Observable<string> = rxPipe(
      that.onLayoutMode,
      opDistinctUntilChanged,
      opShareLast
    );

    // the type
    const onType: Observable<ComponentTypeRef<any>> = (this.onType = rxPipe(
      combineLatest(onLayoutMode, onRenderingContext),
      switchMap<[string, RenderingContext], ComponentTypeRef<any>>(([lm, rc]) =>
        getType(id, lm, rc, aCmp, aMapping)
      ),
      opDistinctComponentTypeRef,
      opShareLast
    ));

    /**
     * Combines the latest changes
     */
    const onComponentState = (this.onState = combineLatest(
      onType,
      onRenderingContext,
      onLayoutMode
    ));

    // done
    handle();
  }

  // just hooks, looks like they are required
  ngOnDestroy() {
    // just dispatch
    super.ngOnDestroy();
  }
}
